import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, Optional, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { PrimeNGConfig } from 'primeng/api';
import { AlertService, AuthenticationService } from '@app/_services';
import { Inbox } from '../inbox';
import { InboxService } from '../inbox.service';
import { Msg, MsgService } from '@app/_components/msg';
import { MsgRecipient } from '@app/_components/msg-recipient/msg-recipient';
import { MsgTemplateService } from '@app/_components/msg-template/msg-template.service';
import { InboxMailSMsComponent } from '../inbox-mail-sms/inbox-mail-sms.component';
import { InboxComponent } from '../inbox/inbox.component';
import { TaskComponent } from '@app/_components/task/task/task.component';
import * as moment from 'moment';
import { User } from '@app/_models';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-conversation',
    templateUrl: './conversation.component.html',
    styleUrls: ['./conversation.component.css'],
    providers: [MessageService],
    standalone: false
})
export class ConversationComponent implements OnInit, OnDestroy {

	@Input() inbox: Inbox;
	@Input() showClose = true;
	@Output() doUpdate = new EventEmitter<any>();
	@Output() tagsChanged = new EventEmitter<{ msgId: number; tags: any[] }>();

	currentUser: User;
	currentUserSubscription: Subscription;

	conversationMessages: any[] = [];
	currentPage = 0;
	pageSize = 10;
	loadingConversation = false;
	hasMoreMessages = true;
	conversationFrom = '';
	conversationTo = '';
	hasSmsData = false;
	hasEmailData = false;
	replyType: 'SMS' | 'EMAIL' = 'SMS';
	showSmsReply = false;
	showEmailReply = false;

	showSmsDetailDialog = false;
	selectedSmsDetail: any = null;
	loadingSmsDetail = false;

	formLoading = false;
	form1: UntypedFormGroup;
	msgTemplates: any[] = [];
	uploadedFiles: any[] = [];
	readonly maxAttachmentTotalSize = 10 * 1024 * 1024;
	openingReservation: { [key: number]: boolean } = {};
	loadingEmailMsgId: number | null = null;
	replyingEmailMsgId: number | null = null;
	conversationLoadError = false;
	private _clientIdCounter = 0;

	/** Tracks which msgIds are currently being archived (per-message spinner). */
	archivingMsgIds = new Set<number>();

	get isSms(): boolean {
		return (this.inbox as any)?.msgOrigin === 2;
	}

	get isEmail(): boolean {
		return (this.inbox as any)?.msgOrigin === 6;
	}

	constructor(
		private primengConfig: PrimeNGConfig,
		private inboxService: InboxService,
		private alertService: AlertService,
		private messageService: MessageService,
		private authenticationService: AuthenticationService,
		private formBuilder: UntypedFormBuilder,
		private dialog: MatDialog,
		private msgService: MsgService,
		private msgTemplateService: MsgTemplateService,
		@Optional() @Inject(MAT_DIALOG_DATA) data: any
	) {
		if (data) {
			if (data.inbox) this.inbox = data.inbox;
			if (data.showClose !== undefined) this.showClose = data.showClose;
		}
		this.currentUserSubscription = this.authenticationService.currentUser.subscribe(user => {
			this.currentUser = user;
		});
	}

	ngOnInit(): void {
		this.primengConfig.ripple = true;
		this.formBuild();
		this.fetchAllTemplates();

		// Initialize reply channels from inbox so composer is usable before conversation loads
		const hasMobile = !!(this.inbox as any)?.senderMobileNum;
		const hasEmail  = !!(this.inbox as any)?.senderEmail;
		if (hasMobile) {
			this.showSmsReply  = true;
			this.showEmailReply = false;
			this.replyType = 'SMS';
		} else if (hasEmail) {
			this.showSmsReply  = false;
			this.showEmailReply = true;
			this.replyType = 'EMAIL';
		}

		setTimeout(() => this.loadConversation(true), 300);
	}

	ngOnDestroy(): void {
		this.currentUserSubscription?.unsubscribe();
	}

	onConversationMessageTagsSelected(msgId: number, tags: any[]): void {
		if (msgId) {
			this.tagsChanged.emit({ msgId, tags: tags ?? [] });
		}
	}

	formBuild(): void {
		this.form1 = this.formBuilder.group({
			mobileText:    [],
			msgTemplateId: [],
			emailSubject:  [''],
			emailHtml:     ['']
		});
	}

	// ===================== Conversation (SMS + Email merged) =====================

	loadConversation(clear = false): void {
		if (!this.inbox) return;

		const recipient      = this.inbox.msgRecipientAPIList?.[0];
		const senderEmail    = this.inbox.senderEmail      || '';
		const recipientEmail = recipient?.email            || '';
		const senderNum      = this.inbox.senderMobileNum  || '';
		const recipientNum   = recipient?.mobileNum        || '';
		const conversationId = this.inbox.msgConversationId || null;

		if (clear) {
			this.currentPage = 0;
			this.conversationMessages = [];
		}
		this.loadingConversation = true;
		this.conversationLoadError = false;

		this.inboxService
			.findGuestConversation(
				senderEmail || null,
				recipientEmail || null,
				senderNum || null,
				recipientNum || null,
				this.currentPage,
				this.pageSize,
				conversationId
			)
			.subscribe((res: any) => {
				const msgs: any[] = res?.data?.msgs || [];
				const mapped = msgs.map(m => ({
					...m,
					isEmailMsg: m.type === 'email'
				}));

				// Sort newest first so latest appears at top
				const merged = [...mapped].sort((a, b) =>
					moment(b.messageDateTime || b.createDate).valueOf() -
					moment(a.messageDateTime || a.createDate).valueOf()
				);

				this.conversationMessages = merged;
				this.hasEmailData = mapped.some(m => m.type === 'email');
				this.hasSmsData   = mapped.some(m => m.type === 'sms');

			// Default reply configuration:
			// - If both SMS and Email exist: show SMS, hide Email (can be toggled on)
			// - If only one exists: show that one
			if (this.hasSmsData && this.hasEmailData) {
				this.showSmsReply = true;
				this.showEmailReply = false;
				this.replyType = 'SMS';
			} else if (this.hasSmsData) {
				this.showSmsReply = true;
				this.showEmailReply = false;
				this.replyType = 'SMS';
			} else if (this.hasEmailData) {
				this.showSmsReply = false;
				this.showEmailReply = true;
				this.replyType = 'EMAIL';
			}
				this.hasMoreMessages  = !!(res?.page ? (res.page.pageNumCurrent < res.page.pageNumLast) : (msgs.length === this.pageSize));
				this.loadingConversation = false;
			}, () => {
				this.loadingConversation = false;
				this.hasMoreMessages = false;
				this.conversationLoadError = true;
			});
	}

	/** Scroll-down: load next page using the same guest-conversion endpoint as loadConversation */
	fetchConversationMessages(): void {
		if (this.loadingConversation || !this.hasMoreMessages) return;

		const recipient      = this.inbox.msgRecipientAPIList?.[0];
		const senderEmail    = this.inbox.senderEmail     || '';
		const recipientEmail = (recipient as any)?.email  || '';
		const senderNum      = this.inbox.senderMobileNum || '';
		const recipientNum   = (recipient as any)?.mobileNum || '';
		const conversationId = this.inbox.msgConversationId || null;

		this.loadingConversation = true;
		this.inboxService
			.findGuestConversation(
				senderEmail    || null,
				recipientEmail || null,
				senderNum      || null,
				recipientNum   || null,
				this.currentPage,
				this.pageSize,
				conversationId
			)
			.subscribe(
				(res: any) => {
					const msgs: any[] = (res?.data?.msgs || []).map((m: any) => ({
						...m,
						isEmailMsg: m.type === 'email'
					}));
					this.conversationMessages = [...this.conversationMessages, ...msgs];
					this.hasMoreMessages = !!(res?.page
						? res.page.pageNumCurrent < res.page.pageNumLast
						: msgs.length === this.pageSize);
					this.loadingConversation = false;
				},
				(err: any) => {
					this.alertService.error(err);
					this.loadingConversation = false;
					// Revert the page increment so the next scroll retries the same page
					this.currentPage--;
				}
			);
	}

	onScrollDown(event: any): void {
		const el = event.target;
		const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
		if (distanceToBottom < 50 && this.hasMoreMessages && !this.loadingConversation) {
			this.currentPage++;
			this.fetchConversationMessages();
		}
	}

	scrollToBottom(): void {
		const el = document.getElementById('conv-chat-messages');
		if (el) el.scrollTop = el.scrollHeight;
	}

	sendSmsMessage(): void {
		const text = this.form1.get('mobileText')?.value?.trim();
		const files = this.uploadedFiles.filter(f => f instanceof File) as File[];
		if (!text && !files.length) return;

		// Guard against double-send: if a send is already in flight, drop the second click/keypress.
		if (this.formLoading) return;
		this.formLoading = true;

		const isSentByUs = this.inbox?.msgFolder === 1;
		const customerRecipient = isSentByUs
			? this.inbox?.msgRecipientAPIList?.[0]
			: { mobileNum: this.inbox?.senderMobileNum, mobileCountryCode: this.inbox?.senderCountryCode };

		let mobileNum = String(customerRecipient?.mobileNum || '').trim();
		const countryCode = String(customerRecipient?.mobileCountryCode || '+1').trim();
		if (mobileNum.startsWith(countryCode)) mobileNum = mobileNum.replace(countryCode, '').trim();

		const clientId = this.addOptimisticMessage('sms', { mobileText: text });
		this.form1.patchValue({ mobileText: '' });
		this.uploadedFiles = [];

		const msgRecipient = new MsgRecipient();
		msgRecipient.mobileNum = mobileNum;
		msgRecipient.mobileCountryCode = countryCode;

		const msg: Msg = new Msg();
		msg.msgRecipientAPIList = [msgRecipient];
		msg.mobileText = text || '';
		msg.topic = this.inbox?.topic as any;
		msg.topicId = this.inbox?.topicId as any;

		const buildPromise = files.length
			? Promise.all(files.map(f => this.fileToBase64(f)))
			: Promise.resolve([]);

		buildPromise.then(attachments => {
			(msg as any).emailAttachments = attachments;
			this.msgService.addAndSendMail(msg).subscribe(
				() => {
					this.formLoading = false;
					this.updateOptimisticStatus(clientId, 'sent');
				},
				() => {
					this.formLoading = false;
					this.updateOptimisticStatus(clientId, 'failed');
				}
			);
		}).catch(() => {
			this.formLoading = false;
			this.updateOptimisticStatus(clientId, 'failed');
		});
	}

	onChatInputEnter(event: KeyboardEvent): void {
		if (!event.shiftKey && this.replyType === 'SMS') {
			event.preventDefault();
			this.sendSmsMessage();
		}
	}

	fetchAllTemplates(): void {
		this.msgTemplateService.findAllWithoutPage().subscribe(
			res => { this.msgTemplates = res.data?.msgTemplates || []; },
			err => { this.alertService.error(err); }
		);
	}

	selectTemplate(event: any): void {
		const selectedId = event.value;
		if (!selectedId) return;
		const tpl = this.msgTemplates?.find(t => t.msgTemplateId === selectedId || t.msgTemplateId === Number(selectedId));
		if (!tpl) return;

		if (this.replyType === 'EMAIL') {
			// Pre-fill subject from template
			if (tpl.subject) this.form1.get('emailSubject')?.setValue(tpl.subject);
			// Prefer HTML body, fall back to plain text
			const body = tpl.emailHtml || tpl.emailText || tpl.mobileText || '';
			if (body) this.form1.get('emailHtml')?.setValue(body);
		} else {
			if (tpl.mobileText) this.form1.get('mobileText')?.setValue(tpl.mobileText);
		}
	}

	onReplyTypeChange(type: 'SMS' | 'EMAIL'): void {
		// Treat these buttons as multi-select toggles for SMS / Email reply UIs.
		if (type === 'SMS') {
			this.showSmsReply = !this.showSmsReply;
		} else {
			this.showEmailReply = !this.showEmailReply;
		}

		// Ensure at least one channel is visible
		if (!this.showSmsReply && !this.showEmailReply) {
			if (this.hasSmsData) {
				this.showSmsReply = true;
			} else if (this.hasEmailData) {
				this.showEmailReply = true;
			}
		}

		// Maintain a primary replyType for Enter key + template behavior.
		if (this.showSmsReply) {
			this.replyType = 'SMS';
		} else if (this.showEmailReply) {
			this.replyType = 'EMAIL';
		}
	}

	sendEmailMessage(): void {
		const subject   = this.form1.get('emailSubject')?.value?.trim();
		const emailHtml = this.form1.get('emailHtml')?.value?.trim();
		if (!emailHtml) {
			this.alertService.error('Please enter a message body before sending.');
			return;
		}

		const isSentByUs = (this.inbox as any)?.msgFolder === 1;
		const toEmail = isSentByUs
			? (this.inbox?.msgRecipientAPIList?.[0] as any)?.email
			: this.inbox?.senderEmail;
		if (!toEmail) {
			this.alertService.error('No recipient email found to reply to.');
			return;
		}

		const clientId = this.addOptimisticMessage('email', { subject, emailHtml });
		this.form1.patchValue({ emailSubject: '', emailHtml: '', msgTemplateId: null });

		const inbox: any = this.inbox;
		const msgRequest: any = {
			mediums: ['EMAIL'],
			emailHtml,
			subject:             subject || '(No subject)',
			imapConfigurationId: inbox?.imapConfigId ?? null,
			replyToMsgId:        inbox?.msgId ?? null,
			msgRecipients:       [{ email: toEmail, emailRecipientType: 'To' }]
		};

		this.inboxService.sendMail(msgRequest).subscribe(
			(res: any) => {
				const sentMsg = res?.data?.msgResponse?.msges?.[0];
				this.updateOptimisticStatus(clientId, 'sent', sentMsg?.msgId ? { msgId: sentMsg.msgId } : {});
			},
			() => this.updateOptimisticStatus(clientId, 'failed')
		);
	}

	openSmsInfo(message: any): void {
		const msgId = message?.msgId;
		const msgRecipientId = message?.msgRecipientId || message?.msgReciepentId || message?.msgRecipientAPIId;
		if (!msgId || !msgRecipientId) {
			this.alertService.error('Unable to load SMS details: missing identifiers.');
			return;
		}
		this.loadingSmsDetail = true;
		this.selectedSmsDetail = null;
		this.showSmsDetailDialog = true;
		this.inboxService.getSmsDetail(msgId, msgRecipientId).subscribe(
			(res: any) => { this.loadingSmsDetail = false; this.selectedSmsDetail = res?.data || res; },
			(_err: any) => {
				this.loadingSmsDetail = false;
				this.selectedSmsDetail = {
					msgId,
					from: message?.mobileNumber || message?.from,
					sentDate: message?.messageDateTime || message?.sentDate,
					mobileText: message?.mobileText,
					_noTrackingId: true
				};
			}
		);
	}

	openSmsAttachment(att: any): void {
		const url = att?.mediaUrl;
		if (url) { window.open(url, '_blank'); return; }
		if (att?.content) {
			const mimeType = att?.type || 'application/octet-stream';
			const bytes = Uint8Array.from(atob(att.content), c => c.charCodeAt(0));
			const blob = new Blob([bytes], { type: mimeType });
			const blobUrl = URL.createObjectURL(blob);
			window.open(blobUrl, '_blank');
			setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
		}
	}

	// ===================== Email Conversation =====================

	loadMockEmailConversation(): void {
		if (!this.inbox) return;

		const base = this.inbox;
		const snippet = (base as any).emailText || base.subject || '(No preview available)';

		this.conversationMessages = [
			{
				...base,
				direction: 'INCOMING',
				messageDateTime: moment().subtract(7, 'days').toISOString(),
				emailSnippet: snippet,
				isEmailMsg: true
			},
			{
				direction: 'OUTGOING',
				messageDateTime: moment().subtract(5, 'days').toISOString(),
				subject: 'Re: ' + (base.subject || ''),
				emailSnippet: 'Thank you for your message. We will look into this and get back to you shortly.',
				senderName: 'KBM Hawaii',
				isEmailMsg: true
			},
			{
				direction: 'INCOMING',
				messageDateTime: moment().subtract(3, 'days').toISOString(),
				subject: base.subject || '',
				emailSnippet: 'Great, please let me know the update as soon as possible. Looking forward to hearing from you.',
				senderName: base.senderName || 'Guest',
				isEmailMsg: true
			},
			{
				...base,
				direction: 'OUTGOING',
				messageDateTime: base.createDate ? moment(base.createDate).toISOString() : moment().toISOString(),
				emailSnippet: snippet,
				senderName: 'KBM Hawaii',
				isEmailMsg: true
			}
		];

		setTimeout(() => this.scrollToBottom(), 200);
	}

	openFullEmail(message: any): void {
		const msgId = message?.msgId;
		if (!msgId) {
			this.alertService.error('Unable to load email: message ID is missing.');
			return;
		}

		this.loadingEmailMsgId = msgId;
		this.msgService.findById(msgId).subscribe({
			next: (res: any) => {
				this.loadingEmailMsgId = null;
				const inboxData = res?.data?.msg;
				if (inboxData && (this.inbox as any)?.reservationAPI) {
					inboxData.reservationAPI = (this.inbox as any).reservationAPI;
				}
				this.openFullEmailDialog(inboxData);
			},
			error: () => {
				this.loadingEmailMsgId = null;
				this.alertService.error('Failed to load email details.');
			}
		});
	}

	openEmailReplyForMessage(message: any): void {
		const msgId = message?.msgId;
		if (!msgId) {
			this.alertService.error('Unable to reply: message ID is missing.');
			return;
		}

		this.replyingEmailMsgId = msgId;
		this.msgService.findById(msgId).subscribe({
			next: (res: any) => {
				this.replyingEmailMsgId = null;
				let inboxData = res?.data?.msg;
				if (inboxData && (this.inbox as any)?.reservationAPI) {
					inboxData.reservationAPI = (this.inbox as any).reservationAPI;
				}
				this.openFullEmailDialog(inboxData, true);
			},
			error: () => {
				this.replyingEmailMsgId = null;
				this.alertService.error('Failed to load email details.');
			}
		});
	}

	private openFullEmailDialog(inboxData: any, autoReply = false): void {
		// InboxComponent constructor requires emailHtml to be non-empty to set this.inbox.
		// If the API only returns emailText (plain-text emails), copy it so the view-mode
		// check passes and the dialog renders identically to the mail tab.
		if (!inboxData?.emailHtml && inboxData?.emailText) {
			inboxData = { ...inboxData, emailHtml: inboxData.emailText };
		}

		const dialogConfig = new MatDialogConfig();
		dialogConfig.width        = '100vw';
		dialogConfig.height       = '100vh';
		dialogConfig.maxWidth     = '100vw';
		dialogConfig.maxHeight    = '100vh';
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus    = true;
		dialogConfig.hasBackdrop  = true;
		dialogConfig.data = {
			title: 'View Inbox Mail',
			inbox: inboxData
		};
		const dialogRef = this.dialog.open(InboxComponent, dialogConfig);

		// On send success: close the dialog and silently refresh the conversation
		// thread so the newly sent message appears inline — no full list reload,
		// no spinner, no selectedMailEntry reset.
		dialogRef.componentInstance.doUpdate.subscribe(() => {
			dialogRef.close();
			this.silentRefreshConversation();
		});

		// doUpdateAndDontClose is fired by sub-actions (e.g. task creation) that
		// should leave the mail dialog open — do not close here.
		dialogRef.componentInstance.doUpdateAndDontClose.subscribe(() => this.doUpdate.emit());

		if (autoReply) {
			setTimeout(() => dialogRef.componentInstance.replyMail(), 300);
		}
	}

	openEmailReply(): void {
		const recipient = this.inbox?.msgRecipientAPIList?.[0];
		const dialogConfig = new MatDialogConfig();
		dialogConfig.width = '100%';
		dialogConfig.height = '100%';
		dialogConfig.maxWidth = '1200px';
		dialogConfig.maxHeight = '800px';
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.data = {
			title: 'Reply Email',
			inbox: this.inbox,
			sendViaEmail: true,
			sendViaSms: false,
			// Pre-fill: reply TO the guest's email (from), using admin mailbox (to) as sender
			senderEmail:    recipient?.email    || this.inbox?.msgRecipientAPIList?.[0]?.email,
			recipientEmail: this.inbox?.senderEmail
		};
		const dialogRef = this.dialog.open(InboxMailSMsComponent, dialogConfig);
		dialogRef.componentInstance.doUpdate.subscribe(() => {
			this.doUpdate.emit();
		});
	}

	silentRefreshConversation(): void {
		if (!this.inbox) return;
		const recipient      = this.inbox.msgRecipientAPIList?.[0];
		const senderEmail    = this.inbox.senderEmail     || '';
		const recipientEmail = (recipient as any)?.email  || '';
		const senderNum      = this.inbox.senderMobileNum || '';
		const recipientNum   = (recipient as any)?.mobileNum || '';
		const conversationId = this.inbox.msgConversationId || null;

		this.inboxService
			.findGuestConversation(
				senderEmail    || null,
				recipientEmail || null,
				senderNum      || null,
				recipientNum   || null,
				0,
				this.pageSize,
				conversationId
			)
			.subscribe((res: any) => {
				const msgs: any[] = res?.data?.msgs || [];
				const mapped = msgs.map((m: any) => ({ ...m, isEmailMsg: m.type === 'email' }));
				this.conversationMessages = [...mapped].sort((a, b) =>
					moment(b.messageDateTime || b.createDate).valueOf() -
					moment(a.messageDateTime || a.createDate).valueOf()
				);
				this.currentPage = 0;
				this.hasMoreMessages = !!(res?.page
					? res.page.pageNumCurrent < res.page.pageNumLast
					: msgs.length === this.pageSize);
			});
	}



	injectNotification(notification: any): void {
		const convId = this.inbox?.msgConversationId;
		if (!convId || !notification?.msgConversationId || notification.msgConversationId !== convId) return;

		// Derive type from msgOrigin (6 = email, 2 = sms) or field presence
		const isEmail = notification.msgOrigin === 6 || (!!notification.senderEmail && !notification.senderMobileNum);
		const type = isEmail ? 'email' : 'sms';

		const message: any = {
			...notification,
			type,
			isEmailMsg: isEmail,
			direction: 'INCOMING',
			messageDateTime: notification.createDate || new Date().toISOString(),
			emailSnippet: isEmail ? this.htmlToSnippet(notification.emailHtml || notification.emailText || '') : undefined
		};

		// Only prepend if not already present (guard against duplicate pushes)
		const alreadyExists = this.conversationMessages.some(m => m.msgId && m.msgId === message.msgId);
		if (!alreadyExists) {
			this.conversationMessages = [message, ...this.conversationMessages];
		}
	}

	private addOptimisticMessage(type: 'sms' | 'email', content: { mobileText?: string; subject?: string; emailHtml?: string }): string {
		const clientId = `local_${Date.now()}_${this._clientIdCounter++}`;
		const now = moment().toISOString();
		const optimistic: any = {
			_clientId:    clientId,
			_sendStatus:  'sending',
			direction:    'OUTGOING',
			type,
			isEmailMsg:   type === 'email',
			messageDateTime: now,
			createDate:   now,
			senderName:   'You',
			mobileText:   content.mobileText || '',
			subject:      content.subject || '',
			emailSnippet: content.emailHtml ? this.htmlToSnippet(content.emailHtml) : ''
		};
		this.conversationMessages = [optimistic, ...this.conversationMessages];
		return clientId;
	}

	private updateOptimisticStatus(clientId: string, status: 'sent' | 'failed', extra: any = {}): void {
		const idx = this.conversationMessages.findIndex(m => m._clientId === clientId);
		if (idx !== -1) {
			this.conversationMessages = this.conversationMessages.map((m, i) =>
				i === idx ? { ...m, _sendStatus: status, ...extra } : m
			);
		}
	}

	private htmlToSnippet(html: string): string {
		const tmp = document.createElement('div');
		tmp.innerHTML = html;
		return (tmp.textContent || tmp.innerText || '').substring(0, 120);
	}

	openCreateTask(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.width        = '100vw';
		dialogConfig.height       = '100vh';
		dialogConfig.maxWidth     = '100vw';
		dialogConfig.maxHeight    = '100vh';
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus    = true;
		dialogConfig.hasBackdrop  = true;
		dialogConfig.data = {
			title:   'Assign Task',
			isModal: true,
			action:  'create',
			purpose: 'createmailtask',
			taskApi: { name: this.inbox?.subject },
			msg:     this.inbox
		};
		const dialogRef = this.dialog.open(TaskComponent, dialogConfig);
		dialogRef.componentInstance.doAdd.subscribe(() => {
			this.messageService.add({ severity: 'success', summary: 'Task Created', detail: 'Task created successfully.' });
			dialogRef.close();
		});
		dialogRef.componentInstance.doUpdate.subscribe(() => dialogRef.close());
	}

	/** Opens the task creation dialog pre-filled with a specific message's context. */
	openTaskForMessage(message: any): void {
		const taskName = message.subject
			|| message.mobileText?.substring(0, 80)
			|| this.inbox?.subject
			|| 'Message Task';

		// Build a minimal inbox-shaped object so TaskComponent gets the right msg reference.
		const msgRef = message.msgId ? { ...this.inbox, msgId: message.msgId, subject: taskName } : this.inbox;

		const dialogConfig = new MatDialogConfig();
		dialogConfig.width        = '100vw';
		dialogConfig.height       = '100vh';
		dialogConfig.maxWidth     = '100vw';
		dialogConfig.maxHeight    = '100vh';
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus    = true;
		dialogConfig.hasBackdrop  = true;
		dialogConfig.data = {
			title:   'Assign Task',
			isModal: true,
			action:  'create',
			purpose: 'createmailtask',
			taskApi: { name: taskName },
			msg:     msgRef
		};
		const dialogRef = this.dialog.open(TaskComponent, dialogConfig);
		dialogRef.componentInstance.doAdd.subscribe(() => {
			this.messageService.add({ severity: 'success', summary: 'Task Created', detail: 'Task created successfully.' });
			dialogRef.close();
		});
		dialogRef.componentInstance.doUpdate.subscribe(() => dialogRef.close());
	}

	/** Archives a specific message in the conversation and removes it from the thread. */
	archiveMessage(message: any): void {
		const msgId = message?.msgId;
		if (!msgId || this.archivingMsgIds.has(msgId)) return;

		this.archivingMsgIds = new Set(this.archivingMsgIds).add(msgId);

		const msg = new Msg();
		(msg as any).msgId     = msgId;
		msg.msgFolder          = 2; // 2 = archive

		this.msgService.archiveMsg(msg).subscribe(
			(res: any) => {
				// Remove the archived message from the visible thread immediately.
				this.conversationMessages = this.conversationMessages.filter(m => m.msgId !== msgId);
				const next = new Set(this.archivingMsgIds);
				next.delete(msgId);
				this.archivingMsgIds = next;
				this.messageService.add({ severity: 'success', summary: 'Archived', detail: res?.message || 'Message archived.' });
				this.doUpdate.emit();
			},
			(err: any) => {
				const next = new Set(this.archivingMsgIds);
				next.delete(msgId);
				this.archivingMsgIds = next;
				this.alertService.error(err);
			}
		);
	}

	
	formatMessageDate(date: any): string {
		if (!date) return '';
		return moment(date).format('MMM DD, YYYY hh:mm A');
	}

	openReservation(reservationId: number): void {
		this.openingReservation[reservationId] = true;
		this.inboxService.getOrderIdByReservationId(reservationId).subscribe(
			(res: any) => {
				window.open(`https://admin.kbmhawaii.com/order/${res?.data?.orderId}`, '_blank');
				this.openingReservation[reservationId] = false;
			},
			() => { this.openingReservation[reservationId] = false; }
		);
	}

	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const files = Array.from(input?.files || []);
		const currentTotal = this.uploadedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
		let running = currentTotal;
		const toAdd: File[] = [];
		for (const file of files) {
			if (running + file.size > this.maxAttachmentTotalSize) break;
			toAdd.push(file);
			running += file.size;
		}
		this.uploadedFiles = [...this.uploadedFiles, ...toAdd];
		if (toAdd.length < files.length) {
			this.messageService.add({ severity: 'info', summary: 'Limit reached', detail: 'Total attachments cannot exceed 10MB.', life: 3000 });
		}
		input.value = '';
	}

	removeAttachment(index: number): void {
		this.uploadedFiles = this.uploadedFiles.filter((_, i) => i !== index);
	}

	viewAttachment(file: File): void {
		const url = URL.createObjectURL(file);
		window.open(url, '_blank');
		setTimeout(() => URL.revokeObjectURL(url), 60000);
	}

	isPdf(file: File): boolean {
		return file?.type === 'application/pdf';
	}

	formatFileSize(bytes: number): string {
		if (!bytes) return '0 B';
		const k = 1024;
		const sizes = ['B', 'KB', 'MB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
	}

	private fileToBase64(file: File): Promise<{ content: string; filename: string; type: string }> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const dataUrl = reader.result as string;
				const base64 = dataUrl.split('base64,')[1] || dataUrl;
				resolve({ content: base64, filename: file.name, type: file.type || 'application/octet-stream' });
			};
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
	}
}
